import React, { useState, useContext, useEffect, useRef } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { Lock, Eye, EyeOff, X, Check, AlertCircle, Loader2 } from 'lucide-react';
import { post } from '../../utils/api';
import AuthContext from '../../context/AuthContext';

import {
  CHANGE_PASSWORD,
} from "../../config/api";

// Password rules, in one place. These used to be duplicated three ways: as prose
// in a grey hint box, as this checklist, and again as the validate() messages —
// so the same four requirements were stated twice on screen and could drift from
// what was actually enforced. The checklist is now the only copy, and validate()
// reads from it.
const RULES = [
  { label: 'At least 6 characters',      test: (p) => p.length >= 6,            error: 'Must be at least 6 characters.' },
  { label: 'A letter (a-z)',             test: (p) => /[a-zA-Z]/.test(p),       error: 'Must contain at least one letter.' },
  { label: 'A number (0-9)',             test: (p) => /[0-9]/.test(p),          error: 'Must contain at least one number.' },
  { label: 'A special character (!@#$)', test: (p) => /[^a-zA-Z0-9]/.test(p),   error: 'Must contain at least one special character.' },
];

const FIELDS = [
  { key: 'oldPassword',     label: 'Current password',     placeholder: 'Enter your current password', autoComplete: 'current-password' },
  { key: 'newPassword',     label: 'New password',         placeholder: 'Enter a new password',        autoComplete: 'new-password' },
  { key: 'confirmPassword', label: 'Confirm new password', placeholder: 'Re-enter the new password',   autoComplete: 'new-password' },
];

const EMPTY = { oldPassword: '', newPassword: '', confirmPassword: '' };

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const authCtx = useContext(AuthContext);

  const [formData, setFormData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState({ oldPassword: false, newPassword: false, confirmPassword: false });
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const [closeHover, setCloseHover] = useState(false);
  const firstFieldRef = useRef(null);

  const [snackConfig, setSnackConfig] = useState({ isOpen: false, type: '', message: '' });

  const handleCloseSnack = () => {
    setSnackConfig((prev) => ({ ...prev, isOpen: false, message: '', type: '' }));
  };

  // ── Reset & close ─────────────────────────────────────────────────────────
  const handleClose = () => {
    setFormData(EMPTY);
    setErrors({});
    setShow({ oldPassword: false, newPassword: false, confirmPassword: false });
    setFocused(null);
    onClose();
  };

  // Escape closes, and focus lands in the first field on open. Neither worked
  // before: this is a hand-rolled div rather than a MUI Dialog, so it got none
  // of that for free and the only way out was the mouse.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKeyDown);
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => { document.removeEventListener('keydown', onKeyDown); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Backdrop clicks never close this. It was `onClick={handleClose}`, so a stray
  // click anywhere outside discarded whatever had been typed; gating that on
  // "only if untouched" wasn't enough either, since an empty form still vanished
  // out from under you mid-task. Leaving is deliberate only: the ✕ in the header
  // or Cancel. Matches utils/dialog.js, which drops backdropClick outright for
  // every other data-entry modal.

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Clear a stale "passwords do not match" as soon as they do match again,
      // rather than making the user submit to find out.
      setErrors((prevErrs) => {
        const errs = { ...prevErrs, [field]: '' };
        if (next.confirmPassword && next.newPassword === next.confirmPassword) errs.confirmPassword = '';
        return errs;
      });
      return next;
    });
  };

  const toggleShow = (field) => setShow((prev) => ({ ...prev, [field]: !prev[field] }));

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.oldPassword) errs.oldPassword = 'Current password is required.';

    if (!formData.newPassword) {
      errs.newPassword = 'New password is required.';
    } else {
      const failed = RULES.find((r) => !r.test(formData.newPassword));
      if (failed) errs.newPassword = failed.error;
      else if (formData.newPassword === formData.oldPassword) {
        errs.newPassword = 'New password must be different from your current password.';
      }
    }

    if (!formData.confirmPassword) errs.confirmPassword = 'Please confirm your new password.';
    else if (formData.newPassword !== formData.confirmPassword) errs.confirmPassword = 'Passwords do not match.';

    return errs;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) return setErrors(errs);

    setLoading(true);
    try {
      const res = await post(CHANGE_PASSWORD, {
        userId: authCtx.currentUser?.id,
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      });

      if (res.responseCode === 200) {
        setSnackConfig({
          isOpen: true,
          message: res.responseMessage || 'Password changed successfully!',
          type: 'success',
        });
        setTimeout(() => handleClose(), 1500);
      } else {
        // The backend maps every non-200 to HTTP 400, so failures normally land
        // in catch below. This covers the case where it ever answers 200 with a
        // failure code — previously that fell through silently and the modal
        // just sat there looking like nothing had happened.
        setSnackConfig({
          isOpen: true,
          message: res.responseMessage || 'Could not change password.',
          type: 'error',
        });
      }
    } catch (error) {
      setSnackConfig({
        isOpen: true,
        message: error.response?.data?.responseMessage || 'Something went wrong.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const confirmMatches =
    formData.confirmPassword.length > 0 && formData.newPassword === formData.confirmPassword;

  return (
    <>
      <Snackbar
        open={snackConfig.isOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnack} severity={snackConfig.type || 'info'} variant="filled" sx={{ width: '100%' }}>
          {snackConfig.message}
        </Alert>
      </Snackbar>

      <div style={S.backdrop} />

      {/* The shell is a flex column capped to the viewport so the body scrolls.
          It was `overflow: hidden` with no height limit before, so once the rule
          checklist and a couple of field errors were showing, the footer could
          sit off-screen with no way to reach Save. */}
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="cpw-title">

        {/* Header — brand gradient, matching the icon tiles, sidebars and
            primary buttons elsewhere. It was a flat #0AAAE6 block. */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <div style={S.iconBox}><Lock size={20} color="#fff" /></div>
            <div style={{ minWidth: 0 }}>
              <h2 id="cpw-title" style={S.title}>Change Password</h2>
              <p style={S.subtitle}>Update your account password</p>
            </div>
          </div>
          {/* Now the primary way out, so it needs to actually look like a
              button — it was white at 15% opacity on the gradient and easy to
              miss entirely. */}
          <button
            type="button"
            style={{ ...S.closeBtn, ...(closeHover ? S.closeBtnHover : null) }}
            onClick={handleClose}
            onMouseEnter={() => setCloseHover(true)}
            onMouseLeave={() => setCloseHover(false)}
            aria-label="Close"
            title="Close"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={S.form}>
          <div style={S.body}>
            {FIELDS.map(({ key, label, placeholder, autoComplete }, i) => {
              const hasError = Boolean(errors[key]);
              const isFocused = focused === key;
              const showMatch = key === 'confirmPassword' && confirmMatches && !hasError;

              return (
                <div key={key} style={S.fieldGroup}>
                  <label htmlFor={`cpw-${key}`} style={S.label}>
                    {label} <span style={{ color: '#DC2626' }} aria-hidden="true">*</span>
                  </label>

                  <div style={{
                    ...S.inputWrapper,
                    borderColor: hasError ? '#DC2626' : showMatch ? '#16A34A' : isFocused ? '#0AAAE6' : '#d1d5db',
                    backgroundColor: isFocused ? '#fff' : '#f9fafb',
                    boxShadow: hasError
                      ? '0 0 0 3px rgba(220,38,38,0.12)'
                      : isFocused ? '0 0 0 3px rgba(10,170,230,0.15)' : 'none',
                  }}>
                    <input
                      id={`cpw-${key}`}
                      ref={i === 0 ? firstFieldRef : undefined}
                      type={show[key] ? 'text' : 'password'}
                      placeholder={placeholder}
                      autoComplete={autoComplete}
                      value={formData[key]}
                      onChange={handleChange(key)}
                      onFocus={() => setFocused(key)}
                      onBlur={() => setFocused(null)}
                      aria-invalid={hasError}
                      aria-describedby={hasError ? `cpw-${key}-error` : undefined}
                      style={S.input}
                    />
                    {showMatch && <Check size={16} color="#16A34A" style={{ flexShrink: 0, marginRight: 6 }} />}
                    <button
                      type="button"
                      onClick={() => toggleShow(key)}
                      style={S.eyeBtn}
                      aria-label={show[key] ? `Hide ${label}` : `Show ${label}`}
                      tabIndex={-1}
                    >
                      {show[key] ? <EyeOff size={17} color="#6b7280" /> : <Eye size={17} color="#6b7280" />}
                    </button>
                  </div>

                  {hasError && (
                    <p id={`cpw-${key}-error`} style={S.errorText}>
                      <AlertCircle size={13} style={{ flexShrink: 0 }} />
                      {errors[key]}
                    </p>
                  )}

                  {/* Requirements live under the new-password field and are always
                      visible while it's in play, so you can see what's still
                      missing instead of discovering it on submit. */}
                  {key === 'newPassword' && (isFocused || formData.newPassword) && (
                    <div style={S.rulesBox}>
                      {RULES.map((rule) => {
                        const passed = rule.test(formData.newPassword);
                        return (
                          <div key={rule.label} style={S.ruleRow}>
                            <span style={{ ...S.ruleDot, ...(passed ? S.ruleDotOn : null) }}>
                              {passed && <Check size={10} color="#fff" strokeWidth={3} />}
                            </span>
                            <span style={{ ...S.ruleText, color: passed ? '#15803D' : '#6b7280' }}>
                              {rule.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={S.footer}>
            <button type="button" style={S.cancelBtn} onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              style={{ ...S.saveBtn, ...(loading ? S.saveBtnLoading : null) }}
              disabled={loading}
            >
              {loading && <Loader2 size={15} style={{ animation: 'cpwSpin 0.8s linear infinite' }} />}
              {loading ? 'Saving…' : 'Update password'}
            </button>
          </div>
        </form>

        <style>{`@keyframes cpwSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </>
  );
};

const S = {
  backdrop: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', zIndex: 1300 },
  modal: {
    position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
    width: 'calc(100% - 32px)', maxWidth: '460px', zIndex: 1301,
    // Cap to the viewport and let the body scroll — see the note at the shell.
    maxHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    background: 'linear-gradient(135deg, #0AAAE6 0%, #6428AA 100%)',
    padding: '18px 20px', flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 },
  iconBox: {
    width: '40px', height: '40px', borderRadius: '11px', flexShrink: 0,
    backgroundColor: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  title: { color: '#fff', fontSize: '17px', fontWeight: 700, margin: 0, lineHeight: 1.3 },
  subtitle: { color: 'rgba(255,255,255,0.85)', fontSize: '12.5px', margin: '2px 0 0', lineHeight: 1.3 },
  closeBtn: {
    background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.45)',
    borderRadius: '50%', width: 34, height: 34, flexShrink: 0, cursor: 'pointer', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background-color 0.15s, border-color 0.15s',
  },
  closeBtnHover: { background: 'rgba(255,255,255,0.38)', borderColor: '#fff' },

  form: { display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 },
  body: { padding: '20px', overflowY: 'auto', flex: 1, minHeight: 0 },

  fieldGroup: { marginBottom: '18px' },
  label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' },
  inputWrapper: {
    display: 'flex', alignItems: 'center', border: '1.5px solid #d1d5db',
    borderRadius: '10px', padding: '0 10px 0 12px',
    transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
  },
  input: {
    flex: 1, minWidth: 0, border: 'none', background: 'transparent',
    padding: '11px 0', fontSize: '14px', color: '#1f2937', outline: 'none',
  },
  eyeBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px', flexShrink: 0,
    display: 'flex', alignItems: 'center', borderRadius: 6,
  },
  errorText: {
    color: '#DC2626', fontSize: '12px', margin: '6px 0 0',
    display: 'flex', alignItems: 'center', gap: 5, lineHeight: 1.4,
  },

  rulesBox: {
    marginTop: '10px', padding: '10px 12px', backgroundColor: '#F8FAFC',
    border: '1px solid #e5e7eb', borderRadius: '10px',
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px 10px',
  },
  ruleRow: { display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 },
  ruleDot: {
    width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
    border: '1.5px solid #d1d5db', backgroundColor: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background-color 0.15s, border-color 0.15s',
  },
  ruleDotOn: { backgroundColor: '#16A34A', borderColor: '#16A34A' },
  ruleText: { fontSize: '11.5px', lineHeight: 1.35 },

  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0,
    padding: '14px 20px', borderTop: '1px solid #f1f5f9', backgroundColor: '#fff',
  },
  cancelBtn: {
    padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #d1d5db',
    backgroundColor: '#fff', color: '#374151', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  },
  saveBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '10px 22px', borderRadius: '10px', border: 'none',
    // Same gradient as PRIMARY_BTN_SX / styles/buttons.js, so this reads as the
    // app's primary action rather than a one-off flat blue.
    background: 'linear-gradient(135deg, #0AAAE6 0%, #6428AA 100%)',
    color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 6px 18px rgba(10,170,230,0.25)',
  },
  saveBtnLoading: { opacity: 0.75, cursor: 'not-allowed', boxShadow: 'none' },
};

export default ChangePasswordModal;
